import {
  CONTENT_PAGE_DEFAULT_SLUGS,
  createCategoryRequestSchema,
  createContentPageRequestSchema,
  createCustomProductRequestSchema,
  createHomeSlideRequestSchema,
  createPopupRequestSchema,
  inviteMemberRequestSchema,
  updateCategoryRequestSchema,
  updateContentPageRequestSchema,
  updateHomeSlideRequestSchema,
  updatePopupRequestSchema,
  updateProductRequestSchema,
  updateStoreBrandingRequestSchema,
  updateStoreHomeRequestSchema,
  type Category,
  type ContentPage,
  type ContentPageKind,
  type HomeSlide,
  type Popup,
  type PopupPlacement,
  type ProductDetail,
  type ProductStatus,
  type StoreBranding,
  type StoreHome,
  type TenantMedia,
  type TenantRole,
} from '@avrahami/contracts';
import { changedProductLists, productListsDraft, type ProductListsDraft } from '@avrahami/ui';
import { he } from '../i18n/he';
import { isoToZonedInput, zonedInputToIso } from './datetime';
import { intOrNaN, parseWith, textOrNull } from './forms';
import { defaultSalePrice, minorToShekels, shekelsToMinor } from './money';

/**
 * Form drafts: what the inputs hold (text as typed), and the requests they become. Every request
 * is checked with the same contract schema the API uses, so most mistakes never leave the browser
 * and the messages land on the right field.
 */

interface ActiveWindow {
  startsAt: string;
  endsAt: string;
}

/** Validity windows are typed and shown in the store's own time zone (its settings). */
function windowDraft(
  value: { startsAt: string | null; endsAt: string | null },
  timeZone: string,
): ActiveWindow {
  return {
    startsAt: isoToZonedInput(value.startsAt, timeZone),
    endsAt: isoToZonedInput(value.endsAt, timeZone),
  };
}

function windowInput(draft: ActiveWindow, timeZone: string) {
  return {
    startsAt: zonedInputToIso(draft.startsAt, timeZone),
    endsAt: zonedInputToIso(draft.endsAt, timeZone),
  };
}

// ---------------------------------------------------------------------------------------------
// Popups
// ---------------------------------------------------------------------------------------------

export interface PopupDraft extends ActiveWindow {
  title: string;
  body: string;
  image: TenantMedia | null;
  buttonLabel: string;
  link: string;
  placement: PopupPlacement;
  isActive: boolean;
}

export function popupDraft(popup: Popup | undefined, timeZone: string): PopupDraft {
  if (popup === undefined) {
    return {
      title: '',
      body: '',
      image: null,
      buttonLabel: '',
      link: '',
      placement: 'ALL_PAGES',
      startsAt: '',
      endsAt: '',
      isActive: true,
    };
  }
  return {
    title: popup.title,
    body: popup.body,
    image: popup.image,
    buttonLabel: popup.buttonLabel ?? '',
    link: popup.link ?? '',
    placement: popup.placement,
    ...windowDraft(popup, timeZone),
    isActive: popup.isActive,
  };
}

function popupFields(draft: PopupDraft, timeZone: string) {
  return {
    title: draft.title,
    body: draft.body,
    mediaId: draft.image?.id ?? null,
    buttonLabel: textOrNull(draft.buttonLabel),
    link: textOrNull(draft.link),
    placement: draft.placement,
    ...windowInput(draft, timeZone),
    isActive: draft.isActive,
  };
}

export function popupCreateRequest(draft: PopupDraft, timeZone: string) {
  return parseWith(createPopupRequestSchema, popupFields(draft, timeZone));
}

export function popupUpdateRequest(draft: PopupDraft, version: number, timeZone: string) {
  return parseWith(updatePopupRequestSchema, { ...popupFields(draft, timeZone), version });
}

// ---------------------------------------------------------------------------------------------
// Home page and slides
// ---------------------------------------------------------------------------------------------

export interface HomeDraft {
  heroEyebrow: string;
  heroTitle: string;
  heroHighlight: string;
  heroText: string;
  heroButtonLabel: string;
  heroButtonLink: string;
  showNewest: boolean;
  featuredCategoryIds: readonly string[];
}

export function homeDraft(home: StoreHome): HomeDraft {
  return {
    heroEyebrow: home.hero.eyebrow ?? '',
    heroTitle: home.hero.title ?? '',
    heroHighlight: home.hero.highlight ?? '',
    heroText: home.hero.text ?? '',
    heroButtonLabel: home.hero.buttonLabel ?? '',
    heroButtonLink: home.hero.buttonLink ?? '',
    showNewest: home.showNewest,
    featuredCategoryIds: home.featuredCategoryIds,
  };
}

/**
 * The home page change. `existingCategoryIds` (when the categories are known) drops featured
 * categories that were deleted meanwhile, which the API would refuse.
 */
export function homeRequest(
  draft: HomeDraft,
  version: number,
  existingCategoryIds: ReadonlySet<string> | null = null,
) {
  return parseWith(updateStoreHomeRequestSchema, {
    hero: {
      eyebrow: textOrNull(draft.heroEyebrow),
      title: textOrNull(draft.heroTitle),
      highlight: textOrNull(draft.heroHighlight),
      text: textOrNull(draft.heroText),
      buttonLabel: textOrNull(draft.heroButtonLabel),
      buttonLink: textOrNull(draft.heroButtonLink),
    },
    showNewest: draft.showNewest,
    featuredCategoryIds:
      existingCategoryIds === null
        ? draft.featuredCategoryIds
        : draft.featuredCategoryIds.filter((id) => existingCategoryIds.has(id)),
    version,
  });
}

export interface SlideDraft {
  image: TenantMedia | null;
  title: string;
  text: string;
  buttonLabel: string;
  link: string;
  position: string;
  isActive: boolean;
}

export function slideDraft(slide?: HomeSlide, nextPosition = 0): SlideDraft {
  return {
    image: slide?.image ?? null,
    title: slide?.title ?? '',
    text: slide?.text ?? '',
    buttonLabel: slide?.buttonLabel ?? '',
    link: slide?.link ?? '',
    position: String(slide?.position ?? nextPosition),
    isActive: slide?.isActive ?? true,
  };
}

function slideFields(draft: SlideDraft) {
  return {
    // A slide needs an image: an empty id is rejected at `mediaId`.
    mediaId: draft.image?.id ?? '',
    title: textOrNull(draft.title),
    text: textOrNull(draft.text),
    buttonLabel: textOrNull(draft.buttonLabel),
    link: textOrNull(draft.link),
    position: intOrNaN(draft.position),
    isActive: draft.isActive,
  };
}

export function slideCreateRequest(draft: SlideDraft) {
  return parseWith(createHomeSlideRequestSchema, slideFields(draft));
}

export function slideUpdateRequest(draft: SlideDraft, version: number) {
  return parseWith(updateHomeSlideRequestSchema, { ...slideFields(draft), version });
}

// ---------------------------------------------------------------------------------------------
// Branding
// ---------------------------------------------------------------------------------------------

export interface BrandingDraft {
  logo: TenantMedia | null;
  favicon: TenantMedia | null;
}

export function brandingDraft(branding: StoreBranding): BrandingDraft {
  return { logo: branding.logo, favicon: branding.favicon };
}

export function brandingRequest(draft: BrandingDraft, version: number) {
  return parseWith(updateStoreBrandingRequestSchema, {
    logoMediaId: draft.logo?.id ?? null,
    faviconMediaId: draft.favicon?.id ?? null,
    version,
  });
}

// ---------------------------------------------------------------------------------------------
// Content pages
// ---------------------------------------------------------------------------------------------

export interface ContentPageDraft {
  kind: ContentPageKind;
  slug: string;
  title: string;
  body: string;
  isPublished: boolean;
  position: string;
}

/** A new page of `kind` starts with its usual address and name. */
export function contentPageDraft(page: ContentPage | undefined, kind: ContentPageKind) {
  if (page !== undefined) {
    return {
      kind: page.kind,
      slug: page.slug,
      title: page.title,
      body: page.body,
      isPublished: page.isPublished,
      position: String(page.position),
    } satisfies ContentPageDraft;
  }
  return {
    kind,
    slug: CONTENT_PAGE_DEFAULT_SLUGS[kind] ?? '',
    title: kind === 'CUSTOM' ? '' : he.pages.kinds[kind],
    body: '',
    isPublished: false,
    position: '0',
  } satisfies ContentPageDraft;
}

function contentPageFields(draft: ContentPageDraft) {
  return {
    kind: draft.kind,
    slug: draft.slug.trim(),
    title: draft.title,
    body: draft.body,
    isPublished: draft.isPublished,
    position: intOrNaN(draft.position),
  };
}

export function contentPageCreateRequest(draft: ContentPageDraft) {
  return parseWith(createContentPageRequestSchema, contentPageFields(draft));
}

export function contentPageUpdateRequest(draft: ContentPageDraft, version: number) {
  return parseWith(updateContentPageRequestSchema, { ...contentPageFields(draft), version });
}

// ---------------------------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------------------------

export interface SpecDraft {
  label: string;
  value: string;
}

export interface ProductDraft extends ProductListsDraft {
  name: string;
  sku: string;
  barcode: string;
  model: string;
  manufacturer: string;
  specs: readonly SpecDraft[];
  extraSpecs: readonly SpecDraft[];
  title: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  /** What the product costs the store; on a catalog product it is shown, never sent. */
  cost: string;
  price: string;
  compareAt: string;
  categoryIds: readonly string[];
  status: ProductStatus;
  /**
   * The lists as the draft was made from them: a list is sent only when it differs, so a list
   * the engine rewrote meanwhile is not sent back as the store's.
   */
  listsBase: ProductListsDraft;
}

export function productDraft(product?: ProductDetail): ProductDraft {
  const lists = productListsDraft(product?.content);
  return {
    name: product?.facts.name ?? '',
    sku: product?.facts.sku ?? '',
    barcode: product?.facts.barcode ?? '',
    model: product?.facts.model ?? '',
    manufacturer: product?.facts.manufacturer ?? '',
    specs: product?.facts.specs ?? [],
    extraSpecs: product?.facts.extraSpecs ?? [],
    title: product?.content.title ?? '',
    description: product?.content.description ?? '',
    seoTitle: product?.content.seoTitle ?? '',
    seoDescription: product?.content.seoDescription ?? '',
    cost: minorToShekels(product?.price.costMinor ?? null),
    price: minorToShekels(product?.price.amountMinor ?? null),
    compareAt: minorToShekels(product?.price.compareAtMinor ?? null),
    categoryIds: product?.categoryIds ?? [],
    status: product?.status ?? 'DRAFT',
    ...lists,
    listsBase: lists,
  };
}

/**
 * Typing the cost of the store's own product. The sale price follows it at the default profit
 * for as long as the store has not set a price of its own: while it is empty, or still the one
 * worked out from the previous cost.
 */
export function costChange(draft: ProductDraft, cost: string): Partial<ProductDraft> {
  const before = shekelsToMinor(draft.cost);
  const now = shekelsToMinor(cost);
  const price = shekelsToMinor(draft.price);
  const follows = price === null || (before !== null && price === defaultSalePrice(before));
  if (!follows || now === null || Number.isNaN(now)) return { cost };
  return { cost, price: minorToShekels(defaultSalePrice(now)) };
}

/** Rows left completely empty are dropped; half-filled rows are kept (and rejected). */
function specRows(rows: readonly SpecDraft[]) {
  return rows
    .map((row) => ({ label: row.label.trim(), value: row.value.trim() }))
    .filter((row) => row.label !== '' || row.value !== '');
}

function factsFields(draft: ProductDraft) {
  return {
    name: draft.name,
    sku: draft.sku,
    barcode: textOrNull(draft.barcode),
    model: textOrNull(draft.model),
    manufacturer: textOrNull(draft.manufacturer),
    specs: specRows(draft.specs),
  };
}

/** Stock is not managed here: a request never carries `inventory`, so the API leaves it alone. */
function contentFields(draft: ProductDraft) {
  return {
    title: textOrNull(draft.title),
    description: textOrNull(draft.description),
    seoTitle: textOrNull(draft.seoTitle),
    seoDescription: textOrNull(draft.seoDescription),
  };
}

/**
 * A product the store adds itself: its prices and sub-categories are chosen once, here. An empty
 * sale price is 0 (the product is shown all the same, with no price for shoppers).
 */
export function productCreateRequest(draft: ProductDraft) {
  return parseWith(createCustomProductRequestSchema, {
    facts: factsFields(draft),
    content: contentFields(draft),
    price: {
      amountMinor: shekelsToMinor(draft.price) ?? 0,
      compareAtMinor: shekelsToMinor(draft.compareAt),
      costMinor: shekelsToMinor(draft.cost),
    },
    categoryIds: draft.categoryIds,
    status: draft.status,
    // Always available for a quote request, whatever the platform's default for new products.
    inventory: { tracked: false },
  });
}

/**
 * The product page. Catalog products keep their copied facts: only the store's own extra rows
 * change. Prices are edited in the product list, and the store never moves a product to another
 * category, so neither is sent: the API leaves both as they are.
 */
export function productUpdateRequest(draft: ProductDraft, product: ProductDetail) {
  const extraSpecs = specRows(draft.extraSpecs);
  return parseWith(updateProductRequestSchema, {
    version: product.version,
    facts: product.facts.locked ? { extraSpecs } : { ...factsFields(draft), extraSpecs },
    content: { ...contentFields(draft), ...changedProductLists(draft, draft.listsBase) },
    status: draft.status,
  });
}

/** What a row of the product list edits in place, as typed. */
export interface ProductRowDraft {
  title: string;
  sku: string;
  cost: string;
  price: string;
}

/** A product as its list row shows it; a price that was never set is 0. */
export function productRowDraft(product: {
  title: string | null;
  sku: string;
  costMinor: number | null;
  amountMinor: number | null;
}): ProductRowDraft {
  return {
    title: product.title ?? '',
    sku: product.sku,
    cost: minorToShekels(product.costMinor),
    price: minorToShekels(product.amountMinor ?? 0),
  };
}

/**
 * The fields of a list row that differ from `saved`, as a product update. The SKU and the cost of
 * a catalog product (`locked`) are the catalog's and never sent. The API replaces a price as a
 * whole, so it goes with the compare-at price the product has while that is still above it. An
 * empty sale price is 0.
 */
export function productRowRequest(
  draft: ProductRowDraft,
  saved: ProductRowDraft,
  product: { version: number; locked: boolean; compareAtMinor: number | null },
) {
  const titleChanged = draft.title !== saved.title;
  const skuChanged = !product.locked && draft.sku !== saved.sku;
  const costChanged = !product.locked && draft.cost !== saved.cost;
  const amountMinor = shekelsToMinor(draft.price) ?? 0;
  const compareAt = product.compareAtMinor;
  return parseWith(updateProductRequestSchema, {
    version: product.version,
    ...(titleChanged ? { content: { title: textOrNull(draft.title) } } : {}),
    ...(skuChanged ? { facts: { sku: draft.sku } } : {}),
    ...(draft.price !== saved.price || costChanged
      ? {
          price: {
            amountMinor,
            compareAtMinor: compareAt !== null && compareAt > amountMinor ? compareAt : null,
            ...(product.locked ? {} : { costMinor: shekelsToMinor(draft.cost) }),
          },
        }
      : {}),
  });
}

// ---------------------------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------------------------

export interface InviteDraft {
  email: string;
  displayName: string;
  role: TenantRole;
}

export function inviteRequest(draft: InviteDraft) {
  return parseWith(inviteMemberRequestSchema, draft);
}

// ---------------------------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------------------------

export interface CategoryDraft {
  name: string;
  /** Empty for a top-level category. */
  parentId: string;
  position: string;
  isVisible: boolean;
  image: TenantMedia | null;
}

export function categoryDraft(category?: Category): CategoryDraft {
  return {
    name: category?.name ?? '',
    parentId: category?.parentId ?? '',
    position: String(category?.position ?? 0),
    isVisible: category?.isVisible ?? true,
    image: category?.image ?? null,
  };
}

function categoryFields(draft: CategoryDraft) {
  return {
    name: draft.name,
    parentId: draft.parentId === '' ? null : draft.parentId,
    position: intOrNaN(draft.position),
    isVisible: draft.isVisible,
    mediaId: draft.image?.id ?? null,
  };
}

export function categoryCreateRequest(draft: CategoryDraft) {
  return parseWith(createCategoryRequestSchema, categoryFields(draft));
}

export function categoryUpdateRequest(draft: CategoryDraft, version: number) {
  return parseWith(updateCategoryRequestSchema, { ...categoryFields(draft), version });
}
